import {
    AfterViewChecked,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    Output,
    Renderer2,
    ViewEncapsulation,
} from '@angular/core';
import { NgClass, NgIf } from '@angular/common';

import { PaginatorModule } from 'primeng/paginator';
import { TranslateModule } from '@ngx-translate/core';

import { common } from '@qwyk/models';

import { PageRequest } from './page-request';

@Component({
    standalone: true,
    imports: [NgIf, NgClass, TranslateModule, PaginatorModule],
    selector: 'qwyk-pagination',
    templateUrl: './pagination.component.html',
    styleUrls: ['./pagination.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class PaginationComponent implements AfterViewChecked {
    /** Fallback page size used whenever a non-numeric / falsy value is received */
    private static readonly DEFAULT_PAGE_SIZE = 25;
    private static readonly FIRST_PAGE_BUTTON_CLASS =
        'p-paginator-simple-first';

    private readonly el = inject(ElementRef);
    private readonly renderer = inject(Renderer2);

    @Input() pagination: common.PaginationMeta | common.SimplePaginationMeta;
    @Input() loading: boolean;
    @Input() onlyPrevAndNextMode = false;
    @Input() resource: string;
    @Input() small = false;
    @Input() showPageLinks = true;
    @Input() showFirstLastIcon = true;
    @Input() showCurrentPageReport = true;
    @Input() showJumpToPageDropdown = true;
    @Input() pageSizeOptions: number[] = [10, 25, 50];
    @Input() wrapperClass = '';
    @Input() simplifiedPagination = false;
    @Input() hasNextPage = false;
    @Output() pageChanged: EventEmitter<PageRequest> =
        new EventEmitter<PageRequest>();

    get effectiveTotalRecords(): number {
        if (
            this.pagination &&
            'total' in this.pagination &&
            this.pagination.total != null
        ) {
            return this.pagination.total;
        }
        return this.hasNextPage ? this.pagination.to + 1 : this.pagination.to;
    }

    ngAfterViewChecked(): void {
        const pagesContainer =
            this.el.nativeElement.querySelector('.p-paginator-pages');
        if (!pagesContainer) return;

        this.syncFirstPageButton(pagesContainer);
        this.syncTrailingEllipsis(pagesContainer);
    }

    private syncTrailingEllipsis(pagesContainer: HTMLElement): void {
        const existing = pagesContainer.querySelector('.p-paginator-ellipsis');

        if (this.hasNextPage && !existing) {
            const btn = this.renderer.createElement('button');
            this.renderer.addClass(btn, 'p-paginator-page');
            this.renderer.addClass(btn, 'p-paginator-element');
            this.renderer.addClass(btn, 'p-link');
            this.renderer.addClass(btn, 'p-paginator-ellipsis');
            this.renderer.setAttribute(btn, 'disabled', '');
            this.renderer.setProperty(btn, 'textContent', '...');
            this.renderer.appendChild(pagesContainer, btn);
        } else if (!this.hasNextPage && existing) {
            this.renderer.removeChild(pagesContainer, existing);
        }
    }

    private syncFirstPageButton(pagesContainer: HTMLElement): void {
        const paginator = pagesContainer.parentElement;
        if (!paginator) return;

        const existing = paginator.querySelector(
            `.${PaginationComponent.FIRST_PAGE_BUTTON_CLASS}`
        );

        if (!this.shouldShowFirstPageButton(pagesContainer)) {
            if (existing) {
                this.renderer.removeChild(paginator, existing);
            }
            return;
        }

        if (existing) return;

        const btn = this.renderer.createElement('button');
        this.renderer.addClass(btn, 'p-paginator-first');
        this.renderer.addClass(btn, 'p-paginator-element');
        this.renderer.addClass(btn, 'p-link');
        this.renderer.addClass(
            btn,
            PaginationComponent.FIRST_PAGE_BUTTON_CLASS
        );
        this.renderer.setAttribute(btn, 'type', 'button');
        this.renderer.setAttribute(btn, 'aria-label', 'First Page');

        const icon = this.renderer.createElement('span');
        this.renderer.addClass(icon, 'p-paginator-icon');
        this.renderer.addClass(icon, 'pi');
        this.renderer.addClass(icon, 'pi-angle-double-left');
        this.renderer.appendChild(btn, icon);

        this.renderer.listen(btn, 'click', () =>
            this.onPageChange({ page: 0, rows: this.pagination.per_page })
        );

        const previousButton = paginator.querySelector('.p-paginator-prev');
        this.renderer.insertBefore(
            paginator,
            btn,
            previousButton ?? pagesContainer
        );
    }

    private shouldShowFirstPageButton(pagesContainer: HTMLElement): boolean {
        if (!this.simplifiedPagination || !this.pagination) return false;

        const firstPageLink = Array.from(
            pagesContainer.querySelectorAll<HTMLButtonElement>(
                '.p-paginator-page:not(.p-paginator-ellipsis)'
            )
        ).find(link => Number.isFinite(Number(link.textContent?.trim())));

        if (!firstPageLink) return false;

        return Number(firstPageLink.textContent?.trim()) >= 2;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onPageChange($event: any) {
        const page = Number($event.page);
        const pageRequest: PageRequest = {
            page: Number.isFinite(page) && page >= 0 ? page + 1 : 1,
            page_size: PaginationComponent.sanitizePageSize($event.rows),
        };
        this.pageChanged.emit(pageRequest);
    }

    /**
     * Guards against 0 / null / false / undefined / NaN (and any other
     * non-numeric value) being used as a page size — falls back to 25.
     */
    static sanitizePageSize(value: unknown): number {
        const size = Number(value);
        return Number.isFinite(size) && size > 0
            ? size
            : PaginationComponent.DEFAULT_PAGE_SIZE;
    }
}
